/* Integrations Vault — admin-only API key vault for all 3rd-party providers.
 *
 * Providers covered (current):
 *  - Unipile         (LinkedIn DM + Email + WhatsApp, live + webhook + accounts picker)
 *  - Apollo          (B2B contacts — already running; surfaced as masked key vault)
 *  - Surfe           (B2B enrichment, live via webhook)
 *  - Fireflies.ai    (meeting intelligence)
 *  - The Org         (public org-charts)
 *  - Perplexity Sonar Pro (real-time grounded web search for signals)
 */
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plug, CheckCircle2, AlertTriangle, RefreshCw, Loader2, Linkedin, Database, Webhook, ChevronDown, ChevronRight, Mic, Network, Sparkles, Globe, Briefcase, Bot, RefreshCcwDot, BrainCircuit, Rocket, Slack as SlackIcon } from "lucide-react";
import { PageHeader } from "./Dashboard";
import { useAuth } from "@/lib/auth";
import ScaliqVault from "@/components/integrations/ScaliqVault";
import { AdvancedSection, ProofPanel, CHANNEL_ICON } from "@/components/integrations/VaultBits";
import UnipileVault from "@/components/integrations/UnipileVault";
import FirefliesVault from "@/components/integrations/FirefliesVault";
import TheOrgVault from "@/components/integrations/TheOrgVault";
import PerplexityVault from "@/components/integrations/PerplexityVault";
import SerperVault from "@/components/integrations/SerperVault";
import ApolloVault from "@/components/integrations/ApolloVault";
import ZevariVault from "@/components/integrations/ZevariVault";
import BrightDataVault from "@/components/integrations/BrightDataVault";
import ApifyVault from "@/components/integrations/ApifyVault";
import KimiVault from "@/components/integrations/KimiVault";
import HubSpotVault from "@/components/integrations/HubSpotVault";
import GenericVault from "@/components/integrations/GenericVault";

const PROVIDERS = [
    { id: "unipile",    name: "Unipile",    color: "var(--jr-brand)", icon: Plug, status: "live" },
    { id: "apollo",     name: "Apollo.io",  color: "var(--jr-info)",  icon: Database, status: "live" },
    { id: "surfe",      name: "Surfe",      color: "var(--jr-info)",  icon: Database, status: "live",
      webhook_info: { path: "/api/webhooks/surfe" } },
    { id: "fireflies",  name: "Fireflies.ai", color: "var(--jr-info)", icon: Mic, status: "live" },
    { id: "theorg",     name: "The Org",    color: "var(--jr-info)",  icon: Network, status: "live" },
    { id: "perplexity", name: "Perplexity Sonar Pro", color: "var(--jr-brand)", icon: Sparkles, status: "live" },
    { id: "serper",     name: "Serper (Google)",       color: "#22c55e",         icon: Globe, status: "live" },
    { id: "zevari",     name: "Zevari",     color: "#38bdf8", icon: Linkedin, status: "live" },
    { id: "brightdata", name: "Bright Data",    color: "#4c8bf5", icon: Briefcase, status: "live" },
    { id: "apify",      name: "Apify",          color: "#97d700", icon: Bot, status: "live" },
    { id: "slack",      name: "Slack",          color: "#e01e5a", icon: SlackIcon, status: "live", key_label: "Webhook URL" },
    { id: "hubspot",    name: "HubSpot",        color: "#ff7a59", icon: RefreshCcwDot, status: "live", key_label: "Private App Token" },
    { id: "kimi",       name: "Kimi (Moonshot AI)", color: "#14e0c8", icon: BrainCircuit, status: "live" },
    { id: "scaliq",     name: "ScalIQ",     color: "#6366f1", icon: Rocket, status: "live" },
];

export default function Integrations() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const isAdmin = user?.role === "owner" || user?.role === "admin";
    const [vault, setVault] = useState(null);
    const [tiers, setTiers] = useState({});
    const [loading, setLoading] = useState(true);
    const [openProvider, setOpenProvider] = useState("unipile");
    const [testingAll, setTestingAll] = useState(false);

    const load = async () => {
        try {
            const [v, t] = await Promise.all([
                api.get("/integrations"),
                api.get("/integrations/linkedin-tiers"),
            ]);
            setVault(v.data); setTiers(t.data);
        } catch (e) { toast.error(t("integr.load_failed")); }
        finally { setLoading(false); }
    };
    const testAll = async () => {
        setTestingAll(true);
        try {
            const r = await api.post("/integrations/health/all");
            const results = r.data.results || {};
            const ok = Object.values(results).filter((x) => x.ok).length;
            const failed = Object.entries(results).filter(([, x]) => !x.ok);
            if (failed.length === 0) {
                toast.success(t("integr.healthcheck_result", { ok, fail: 0 }));
            } else {
                const names = failed
                    .map(([id]) => PROVIDERS.find((p) => p.id === id)?.name || id)
                    .join(", ");
                toast.error(t("integr.healthcheck_result_fail", { ok, fail: failed.length, names }), { duration: 8000 });
            }
            await load();
        } catch (e) { toast.error(t("integr.healthcheck_failed")); }
        finally { setTestingAll(false); }
    };
    useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

    // Apollo MCP OAuth-callback afhandeling (?apollo_mcp=connected | ?apollo_mcp_error=…)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("apollo_mcp") === "connected") {
            toast.success(t("integr.mcp_connected_toast"));
            setOpenProvider("apollo");
        } else if (params.get("apollo_mcp_error")) {
            toast.error(t("integr.mcp_error_toast", { err: params.get("apollo_mcp_error") }), { duration: 8000 });
            setOpenProvider("apollo");
        } else { return; }
        window.history.replaceState({}, "", window.location.pathname);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Falende integraties afgeleid uit de vault (persistente last_status) —
    // zichtbaar bij page-load én na "Test alle keys".
    const failingProviders = useMemo(() => {
        if (!vault) return [];
        return PROVIDERS
            .filter((p) => (vault[p.id] || {}).configured && vault[p.id].last_status === "fail")
            .map((p) => ({ id: p.id, name: p.name, message: vault[p.id].last_status_message || "" }));
    }, [vault]);

    const jumpToProvider = (id) => {
        setOpenProvider(id);
        setTimeout(() => {
            document.getElementById(`provider-card-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
    };

    // Auto-poll vault every 30s while page is open — picks up background
    // health checks triggered by other admins or the auto-save hook.
    useEffect(() => {
        if (!isAdmin) return;
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [isAdmin]);

    if (!isAdmin) {
        return (
            <PageHeader title={t("integr.title")} subtitle={t("integr.admin_only")}>
                <div className="text-sm text-[var(--jr-text-2)]">{t("integr.no_access")}</div>
            </PageHeader>
        );
    }

    return (
        <PageHeader
            title={t("integr.title")}
            subtitle={t("integr.subtitle")}
            actions={
                <div className="flex items-center gap-2">
                    <button onClick={testAll} disabled={testingAll} data-testid="integrations-test-all"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-mono uppercase tracking-[0.16em] bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] border border-[var(--jr-brand)]/30 hover:bg-[var(--jr-brand)]/25 disabled:opacity-50">
                        {testingAll ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} {t("integr.test_all")}
                    </button>
                    <button onClick={load} data-testid="integrations-refresh"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-mono uppercase tracking-[0.16em] text-[var(--jr-text-2)] hover:text-white border border-white/10 hover:bg-white/5">
                        <RefreshCw size={12} /> {t("common.refresh")}
                    </button>
                </div>
            }
        >
            {loading || !vault ? (
                <div className="text-[var(--jr-text-2)]">{t("common.loading")}</div>
            ) : (
                <div className="space-y-4 max-w-4xl">
                    {failingProviders.length > 0 && (
                        <div data-testid="integrations-failing-banner"
                            className="rounded-2xl border border-[#ef4444]/40 bg-[#ef4444]/[0.07] p-4 space-y-2">
                            <div className="flex items-center gap-2 text-[#ef4444] text-sm font-semibold">
                                <AlertTriangle size={16} />
                                {t("integr.failing_banner_title", { count: failingProviders.length })}
                            </div>
                            {failingProviders.map((f) => (
                                <div key={f.id} data-testid={`failing-provider-${f.id}`}
                                    className="flex items-start justify-between gap-3 pl-6">
                                    <div className="min-w-0 text-sm">
                                        <span className="font-semibold text-[var(--jr-text-1,inherit)]">{f.name}</span>
                                        {f.message && <span className="text-[var(--jr-text-2)]"> — {f.message}</span>}
                                    </div>
                                    <button onClick={() => jumpToProvider(f.id)}
                                        data-testid={`failing-provider-view-${f.id}`}
                                        className="shrink-0 px-2.5 py-1 rounded-full text-[11px] font-mono uppercase tracking-[0.12em] text-[#ef4444] border border-[#ef4444]/40 hover:bg-[#ef4444]/15 transition-colors">
                                        {t("integr.failing_banner_view")}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    {PROVIDERS.map((p) => (
                        <div key={p.id} id={`provider-card-${p.id}`}>
                            <ProviderCard provider={p}
                                cfg={vault[p.id] || {}}
                                tiers={tiers}
                                isOpen={openProvider === p.id}
                                onToggle={() => setOpenProvider(openProvider === p.id ? null : p.id)}
                                onSaved={load}
                                webhookUrl={vault._webhook_url} />
                        </div>
                    ))}
                </div>
            )}
        </PageHeader>
    );
}

/* ----------------------------------------------------------------- */
/*  Provider Card — compacte kaart (Emergent-stijl): status + test    */
/*  vooraan, API-key direct zichtbaar, de rest achter "Geavanceerd".  */
/* ----------------------------------------------------------------- */
function ProviderCard({ provider, cfg, tiers, isOpen, onToggle, onSaved, webhookUrl }) {
    const { t } = useTranslation();
    const Icon = provider.icon;
    const [testing, setTesting] = useState(false);

    // Unified status: groen = gekoppeld & test geslaagd · rood = test faalt ·
    // amber = key aanwezig maar nog niet getest · grijs = niet gekoppeld.
    const status = !cfg.configured ? "none"
        : cfg.last_status === "ok" ? "ok"
        : cfg.last_status === "fail" ? "fail" : "idle";
    const S = {
        ok:   { dot: "bg-[#22c55e]", cls: "bg-[#22c55e]/12 text-[#22c55e] border border-[#22c55e]/30", label: t("integr.status_connected") },
        fail: { dot: "bg-[#ef4444]", cls: "bg-[#ef4444]/12 text-[#ef4444] border border-[#ef4444]/30", label: t("integr.health_fail") },
        idle: { dot: "bg-[var(--jr-amber)]", cls: "bg-[var(--jr-amber)]/12 text-[var(--jr-amber)] border border-[var(--jr-amber)]/30", label: t("integr.health_idle") },
        none: { dot: "bg-[var(--jr-text-3)]", cls: "bg-white/[0.04] text-[var(--jr-text-3)] border border-white/10", label: t("integr.not_configured") },
    }[status];

    const testConnection = async (e) => {
        e.stopPropagation(); e.preventDefault();
        setTesting(true);
        try {
            const r = await api.post(`/integrations/${provider.id}/health`);
            if (r.data.ok) toast.success(`${provider.name}: ${r.data.message}`);
            else toast.error(`${provider.name}: ${r.data.message}`);
            onSaved?.();
        } catch (e2) { toast.error(e2?.response?.data?.detail || t("integr.test_failed")); }
        finally { setTesting(false); }
    };

    return (
        <div className={`rounded-2xl border ${status === "ok" ? "border-[#22c55e]/25" : cfg.configured ? "border-[var(--jr-brand)]/25" : "border-white/10"} bg-[var(--jr-surface)] overflow-hidden`}
            data-testid={`provider-${provider.id}`}>
            {/* Header — clickable to toggle (div + role=button to allow nested test button) */}
            <div onClick={onToggle} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle?.(); } }}
                role="button" tabIndex={0} data-testid={`toggle-${provider.id}`}
                className="w-full text-left flex items-center gap-3 p-4 hover:bg-white/[0.02] cursor-pointer select-none">
                <span className="w-9 h-9 rounded-xl grid place-items-center shrink-0"
                    style={{ background: provider.color + "1A", color: provider.color }}>
                    <Icon size={18} />
                </span>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display text-base tracking-tight text-white">{provider.name}</h3>
                        <span className={`text-[10px] font-mono uppercase tracking-[0.16em] px-2 py-0.5 rounded-full inline-flex items-center gap-1.5 ${S.cls}`}
                            data-testid={`configured-badge-${provider.id}`}
                            title={cfg.last_status_message || ""}>
                            <span className={`w-1.5 h-1.5 rounded-full ${S.dot} ${status === "ok" ? "animate-pulse" : ""}`} />
                            {S.label}
                        </span>
                        {cfg.key_source === "env" && (
                            <span className="text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[var(--jr-info)]/12 text-[var(--jr-info)] border border-[var(--jr-info)]/25"
                                data-testid={`env-badge-${provider.id}`}>
                                {t("integr.via_env")}
                            </span>
                        )}
                    </div>
                    <p className="text-[11px] text-[var(--jr-text-3)] mt-0.5 truncate">{t(`integr.desc.${provider.id}`)}</p>
                </div>
                {cfg.configured && (
                    <button onClick={testConnection} disabled={testing}
                        data-testid={`health-test-${provider.id}`}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium border border-[#22c55e]/40 text-[#22c55e] hover:bg-[#22c55e]/10 disabled:opacity-50 shrink-0">
                        {testing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                        {t("integr.test_connection")}
                    </button>
                )}
                {isOpen ? <ChevronDown size={18} className="text-[var(--jr-text-3)] shrink-0" /> : <ChevronRight size={18} className="text-[var(--jr-text-3)] shrink-0" />}
            </div>

            {/* Body */}
            {isOpen && (
                <div className="px-5 pb-5 pt-3 border-t border-white/8" data-testid={`body-${provider.id}`}>
                    {cfg.last_status_message && status !== "none" && (
                        <div className={`mb-4 rounded-lg px-3 py-2 text-xs flex items-start gap-2 ${status === "fail"
                            ? "border border-[#ef4444]/25 bg-[#ef4444]/[0.06] text-[var(--jr-text-2)]"
                            : "border border-[#22c55e]/20 bg-[#22c55e]/[0.05] text-[var(--jr-text-2)]"}`}
                            data-testid={`status-msg-${provider.id}`}>
                            {status === "fail"
                                ? <AlertTriangle size={13} className="text-[#ef4444] mt-0.5 shrink-0" />
                                : <CheckCircle2 size={13} className="text-[#22c55e] mt-0.5 shrink-0" />}
                            <span className="min-w-0">{cfg.last_status_message}</span>
                        </div>
                    )}
                    {provider.id === "unipile" ? (
                        <UnipileVault cfg={cfg} tiers={tiers} webhookUrl={webhookUrl} onSaved={onSaved} />
                    ) : provider.id === "fireflies" ? (
                        <FirefliesVault cfg={cfg} onSaved={onSaved} />
                    ) : provider.id === "theorg" ? (
                        <TheOrgVault cfg={cfg} onSaved={onSaved} />
                    ) : provider.id === "perplexity" ? (
                        <PerplexityVault cfg={cfg} onSaved={onSaved} />
                    ) : provider.id === "serper" ? (
                        <SerperVault cfg={cfg} onSaved={onSaved} />
                    ) : provider.id === "apollo" ? (
                        <ApolloVault cfg={cfg} onSaved={onSaved} />
                    ) : provider.id === "zevari" ? (
                        <ZevariVault provider={provider} cfg={cfg} onSaved={onSaved} />
                    ) : provider.id === "brightdata" ? (
                        <BrightDataVault provider={provider} cfg={cfg} onSaved={onSaved} />
                    ) : provider.id === "apify" ? (
                        <ApifyVault provider={provider} cfg={cfg} onSaved={onSaved} />
                    ) : provider.id === "kimi" ? (
                        <KimiVault provider={provider} cfg={cfg} onSaved={onSaved} />
                    ) : provider.id === "scaliq" ? (
                        <ScaliqVault cfg={cfg} onSaved={onSaved}
                            appBaseUrl={webhookUrl?.replace(/\/api\/webhooks\/unipile$/, "") || ""} />
                    ) : provider.id === "hubspot" ? (
                        <HubSpotVault provider={provider} cfg={cfg} onSaved={onSaved}
                            appBaseUrl={webhookUrl?.replace(/\/api\/webhooks\/unipile$/, "") || ""} />
                    ) : (
                        <GenericVault provider={provider} cfg={cfg} onSaved={onSaved} appBaseUrl={webhookUrl?.replace(/\/api\/webhooks\/unipile$/, "") || ""} />
                    )}
                    <AdvancedSection id={provider.id}>
                        <ProofPanel provider={provider} cfg={cfg} />
                    </AdvancedSection>
                </div>
            )}
        </div>
    );
}
